import moment from 'moment';

export default {
  data() {
    return {
      dragState: {
        active: false,
        dragging: false,
        appointment: null,
        element: null,
        preview: null,
        pointerId: null,
        offsetX: 0,
        offsetY: 0,
        startX: 0,
        startY: 0,
        lastClientX: null,
        lastClientY: null,
        blockClickListener: null,
      },
      dragHandlers: {
        move: null,
        up: null,
      },
      dragSavingId: null,
    };
  },
  methods: {
    canDragAppointment(appointment) {
      if (!appointment) {
        return false;
      }
      if (!appointment.id) {
        return false;
      }
      if (!appointment.patient || !appointment.patient.name) {
        return false;
      }
      if (this.dragSavingId && this.dragSavingId === appointment.id) {
        return false;
      }
      return true;
    },
    isDraggingAppointment(appointment) {
      if (!this.dragState.dragging || !this.dragState.appointment || !appointment) {
        return false;
      }
      return this.dragState.appointment.id === appointment.id;
    },
    isSavingAppointment(appointment) {
      if (!appointment) {
        return false;
      }
      return this.dragSavingId === appointment.id;
    },
    ensureDragHandlers() {
      if (!this.dragHandlers.move) {
        this.dragHandlers.move = this.handlePointerMove.bind(this);
      }
      if (!this.dragHandlers.up) {
        this.dragHandlers.up = this.handlePointerUp.bind(this);
      }
    },
    onEventPointerDown(event, calendarEvent) {
      if (!this.canDragAppointment(calendarEvent.appointment)) {
        return;
      }
      if (event.button !== undefined && event.button !== 0) {
        return;
      }
      const element = event.currentTarget;
      const rect = element.getBoundingClientRect();
      this.dragState.active = true;
      this.dragState.dragging = false;
      this.dragState.appointment = calendarEvent.appointment;
      this.dragState.element = element;
      this.dragState.pointerId = event.pointerId || 'mouse';
      this.dragState.offsetX = event.clientX - rect.left;
      this.dragState.offsetY = event.clientY - rect.top;
      this.dragState.startX = event.clientX;
      this.dragState.startY = event.clientY;
      this.dragState.lastClientX = event.clientX;
      this.dragState.lastClientY = event.clientY;
      this.ensureDragHandlers();
      document.addEventListener('pointermove', this.dragHandlers.move);
      document.addEventListener('pointerup', this.dragHandlers.up);
      document.addEventListener('pointercancel', this.dragHandlers.up);
    },
    handlePointerMove(event) {
      if (!this.dragState.active) {
        return;
      }
      if (event.pointerId && event.pointerId !== this.dragState.pointerId) {
        return;
      }
      this.dragState.lastClientX = event.clientX;
      this.dragState.lastClientY = event.clientY;
      const deltaX = Math.abs(event.clientX - this.dragState.startX);
      const deltaY = Math.abs(event.clientY - this.dragState.startY);
      if (!this.dragState.dragging) {
        if (deltaX < 3 && deltaY < 3) {
          return;
        }
        this.startDraggingPreview();
        this.blockNextClick();
      }
      this.updatePreviewPosition(event.clientX, event.clientY);
    },
    handlePointerUp(event) {
      if (!this.dragState.active) {
        return;
      }
      if (event.pointerId && event.pointerId !== this.dragState.pointerId) {
        return;
      }
      const wasDragging = this.dragState.dragging;
      const position = {
        x: this.dragState.lastClientX,
        y: this.dragState.lastClientY,
      };
      const { appointment } = this.dragState;
      this.detachDragListeners();
      this.removePreview();
      this.clearDragElementState();
      this.resetDragState();
      if (!wasDragging || !appointment || position.x === null || position.y === null) {
        return;
      }
      const targetEvent = this.getEventFromPoint(position.x, position.y);
      if (targetEvent) {
        if (this.isAvailableAppointment(targetEvent.appointment)) {
          this.pasteDraggedAppointment(targetEvent.appointment, appointment);
        }
        return;
      }
      const context = this.getDropContext(position.x, position.y);
      if (!context) {
        return;
      }
      this.moveAppointmentTo(context, appointment, position);
    },
    startDraggingPreview() {
      if (!this.dragState.element) {
        return;
      }
      this.dragState.dragging = true;
      this.dragState.element.classList.add('week-calendar-event--active-drag');
      const preview = this.dragState.element.cloneNode(true);
      preview.style.position = 'fixed';
      preview.style.left = `${this.dragState.lastClientX - this.dragState.offsetX}px`;
      preview.style.top = `${this.dragState.lastClientY - this.dragState.offsetY}px`;
      preview.style.width = `${this.dragState.element.offsetWidth}px`;
      preview.style.height = `${this.dragState.element.offsetHeight}px`;
      preview.style.pointerEvents = 'none';
      preview.style.zIndex = '10000';
      preview.style.opacity = '0.9';
      document.body.appendChild(preview);
      this.dragState.preview = preview;
    },
    updatePreviewPosition(x, y) {
      if (!this.dragState.preview) {
        return;
      }
      this.dragState.preview.style.left = `${x - this.dragState.offsetX}px`;
      this.dragState.preview.style.top = `${y - this.dragState.offsetY}px`;
    },
    detachDragListeners() {
      if (this.dragHandlers.move) {
        document.removeEventListener('pointermove', this.dragHandlers.move);
      }
      if (this.dragHandlers.up) {
        document.removeEventListener('pointerup', this.dragHandlers.up);
        document.removeEventListener('pointercancel', this.dragHandlers.up);
      }
    },
    removePreview() {
      if (this.dragState.preview && this.dragState.preview.parentNode) {
        this.dragState.preview.parentNode.removeChild(this.dragState.preview);
      }
      this.dragState.preview = null;
    },
    clearDragElementState() {
      if (this.dragState.element) {
        this.dragState.element.classList.remove('week-calendar-event--active-drag');
      }
    },
    resetDragState() {
      this.dragState.active = false;
      this.dragState.dragging = false;
      this.dragState.appointment = null;
      this.dragState.element = null;
      this.dragState.pointerId = null;
      this.dragState.offsetX = 0;
      this.dragState.offsetY = 0;
      this.dragState.startX = 0;
      this.dragState.startY = 0;
      this.dragState.lastClientX = null;
      this.dragState.lastClientY = null;
    },
    blockNextClick() {
      if (this.dragState.blockClickListener) {
        return;
      }
      this.dragState.blockClickListener = (event) => {
        event.stopPropagation();
        event.preventDefault();
        document.removeEventListener('click', this.dragState.blockClickListener, true);
        this.dragState.blockClickListener = null;
      };
      document.addEventListener('click', this.dragState.blockClickListener, true);
    },
    getDropContext(x, y) {
      if (!this.$el) {
        return null;
      }
      const columns = this.$el.querySelectorAll('.week-calendar-day-column');
      for (let i = 0; i < columns.length; i += 1) {
        const rect = columns[i].getBoundingClientRect();
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          return {
            index: i,
            rect,
            date: this.days[i] ? this.days[i].date : null,
          };
        }
      }
      return null;
    },
    getEventFromPoint(x, y) {
      const element = document.elementFromPoint(x, y);
      if (!element) {
        return null;
      }
      const eventElement = element.closest('.week-calendar-event');
      if (!eventElement) {
        return null;
      }
      const { eventKey, eventDate } = eventElement.dataset;
      if (!eventKey || !eventDate) {
        return null;
      }
      const groups = this.dayEventMap[eventDate] || [];
      for (let i = 0; i < groups.length; i += 1) {
        const found = groups[i].events.find(item => item.key === eventKey);
        if (found) {
          return found;
        }
      }
      return null;
    },
    isAvailableAppointment(appointment) {
      if (!appointment) {
        return false;
      }
      if (appointment.status) {
        return appointment.status === 'available';
      }
      return !(appointment.patient && appointment.patient.name);
    },
    snapMinutes(value) {
      const step = this.minEventMinutes || 5;
      if (step <= 0) {
        return value;
      }
      return Math.round(value / step) * step;
    },
    moveAppointmentTo(context, appointment, position) {
      if (!context.date) {
        return;
      }
      const minutes = this.getMinutesFromPosition(context.rect, position.y);
      const startMoment = moment(context.date)
        .hour(this.timeRange.minHour)
        .minute(0)
        .second(0)
        .add(minutes, 'minutes');
      const originalStart = moment(appointment.startDate);
      if (!originalStart.isValid()) {
        return;
      }
      const duration = this.getAppointmentDuration(appointment);
      const newEnd = startMoment.clone().add(duration, 'minutes');
      if (startMoment.isSame(originalStart)) {
        return;
      }
      this.persistAppointmentMove(appointment, startMoment, newEnd);
    },
    getMinutesFromPosition(rect, clientY) {
      if (!rect) {
        return 0;
      }
      const offset = Math.min(Math.max(clientY - rect.top, 0), rect.height);
      const minutes = offset / this.minuteHeight;
      const snapped = this.snapMinutes(minutes);
      const clamped = Math.min(Math.max(snapped, 0), this.totalMinutes);
      return clamped;
    },
    selectProfessionalForTarget(professionals, sourceProfessional) {
      if (!Array.isArray(professionals) || professionals.length === 0) {
        return null;
      }
      const matched = sourceProfessional
        ? professionals.find(({ id }) => id === sourceProfessional.id)
        : null;
      const selected = matched || professionals[0];
      const data = {
        id: selected.id,
        specialty: undefined,
      };
      const specialties = selected.specialties || [];
      if (specialties.length > 0) {
        if (
          sourceProfessional
          && sourceProfessional.specialty
          && specialties.includes(sourceProfessional.specialty)
        ) {
          data.specialty = sourceProfessional.specialty;
        } else {
          [data.specialty] = specialties;
        }
      }
      return data;
    },
    async pasteDraggedAppointment(targetAppointment, draggedAppointment) {
      if (!targetAppointment || !draggedAppointment || !draggedAppointment.id) {
        return;
      }
      const targetStart = targetAppointment.startDate ? moment(targetAppointment.startDate) : null;
      if (!targetStart || !targetStart.isValid()) {
        return;
      }
      if (targetStart.isBefore(moment(), 'day')) {
        this.$toast.show('Não é possível copiar o agendamento para uma data anterior à hoje.', {
          type: 'error',
          timeout: 10000,
        });
        return;
      }
      const availableInsurancePlans = (
        targetAppointment.available
        && Array.isArray(targetAppointment.available.insurancePlans)
      )
        ? targetAppointment.available.insurancePlans
        : [];
      const sourceInsurancePlanId = draggedAppointment.insurance
        && draggedAppointment.insurance.plan
        ? draggedAppointment.insurance.plan.id
        : null;
      if (
        sourceInsurancePlanId
        && availableInsurancePlans.length > 0
        && !availableInsurancePlans.includes(sourceInsurancePlanId)
      ) {
        this.$toast.show('Este horário não está disponível para o convênio do agendamento original.', {
          type: 'error',
          timeout: 10000,
        });
        return;
      }
      const targetProfessionals = (
        targetAppointment.available
        && Array.isArray(targetAppointment.available.professionals)
      )
        ? targetAppointment.available.professionals
        : [];
      const professionalData = this.selectProfessionalForTarget(
        targetProfessionals,
        draggedAppointment.professional,
      );
      if (!professionalData) {
        this.$toast.show('Nenhum profissional disponível para este horário.', {
          type: 'error',
          timeout: 10000,
        });
        return;
      }
      const payload = {
        scheduleId: targetAppointment.scheduleId,
        startDate: targetAppointment.startDate,
        endDate: targetAppointment.endDate,
        professional: professionalData,
        patient: draggedAppointment.patient,
        insurance: {
          id: draggedAppointment.insurance ? draggedAppointment.insurance.id : null,
          record: draggedAppointment.insurance ? draggedAppointment.insurance.record : null,
          validity: draggedAppointment.insurance ? draggedAppointment.insurance.validity : null,
          planId: draggedAppointment.insurance && draggedAppointment.insurance.plan
            ? draggedAppointment.insurance.plan.id
            : null,
        },
        type: targetAppointment.type || draggedAppointment.type,
        status: 'scheduled',
        expenses: (draggedAppointment.expenses || []).map((item) => {
          const expense = { ...item };
          expense.isNew = true;
          expense.return = false;
          return expense;
        }),
        notes: draggedAppointment.notes || '',
      };
      try {
        const { data } = await this.$httpX.post('/appointments', payload, {
          params: { toCalendar: true },
        });
        this.$store.dispatch('upsert', data);
        this.$toast.show('Agendamento copiado', { type: 'success' });
      } catch (error) {
        this.$toast.show('Não foi possível copiar o agendamento', { type: 'error' });
      }
    },
    getAppointmentDuration(appointment) {
      const start = moment(appointment.startDate);
      const end = appointment.endDate ? moment(appointment.endDate) : null;
      if (start.isValid() && end && end.isValid() && end.isAfter(start)) {
        return end.diff(start, 'minutes');
      }
      return this.minEventMinutes;
    },
    async persistAppointmentMove(appointment, startMoment, endMoment) {
      if (!appointment || !appointment.id) {
        return;
      }
      const payload = {
        startDate: startMoment.format('YYYY-MM-DD[T]HH:mm'),
        endDate: endMoment.format('YYYY-MM-DD[T]HH:mm'),
      };
      this.dragSavingId = appointment.id;
      try {
        const { data } = await this.$httpX.put(`/appointments/${appointment.id}`, payload, {
          params: { toCalendar: true },
        });
        this.$store.dispatch('upsert', data);
        this.$toast.show('Agendamento atualizado', { type: 'success' });
      } catch (e) {
        this.$toast.show('Não foi possível mover o agendamento', { type: 'error' });
      } finally {
        this.dragSavingId = null;
      }
    },
  },
  beforeDestroy() {
    this.detachDragListeners();
    this.removePreview();
    this.clearDragElementState();
  },
};
